import type { PrivateSellerRatingsProps } from './PrivateSellerRatings.typed';
import * as Styled from './PrivateSellerRatings.styled';
import { InfoTitle } from 'components/Toolkit/InfoTitle/InfoTitle';
import { StarIc } from '@dsch/dd-icons';
import { useTheme } from 'styled-components';
import { LinkButton } from 'components/Toolkit/Button/LinkButton';
import { plural } from 'helpers/plural';

const PrivateSellerRatings = (props: PrivateSellerRatingsProps) => {
  const {
    membershipDuration,
    userRating,
    className,
    onClickRatings,
    onClickReviews,
  } = props;
  const theme = useTheme();
  const hasRating = Boolean(userRating);
  const { average, reviewCount, ratingCategory } = userRating || {
    average: '',
    reviewCount: 0,
  };
  const hasReviews = reviewCount > 0;

  const ratingContent = (
    <>
      {average && `${average}/5`}
      <Styled.RatingStarIc color={theme.colors.ORANGE} width="16" height="16" />
    </>
  );

  const getRatingTitle = () => {
    if (!hasRating) {
      return (
        <Styled.IconWrapper>
          <StarIc color={theme.colors.GREY_DARKER} width="16" height="16" />
        </Styled.IconWrapper>
      );
    }

    if (onClickRatings) {
      return (
        <Styled.Rating onClick={onClickRatings} name="rating">
          {ratingContent}
        </Styled.Rating>
      );
    }

    return <Styled.RatingNoLink>{ratingContent}</Styled.RatingNoLink>;
  };

  const getReviewTitle = () => {
    if (!hasReviews) {
      return '0';
    }

    if (onClickReviews) {
      return (
        <LinkButton onClick={onClickReviews} fontSize="M16">
          {reviewCount}
        </LinkButton>
      );
    }

    return <>{reviewCount}</>;
  };

  return (
    <Styled.PrivateSellerRatings className={className}>
      <Styled.RatingSection>
        <InfoTitle
          dataTestId="rating"
          title={getRatingTitle()}
          description={ratingCategory ?? <>No rating</>}
          variant="SMALL"
        />
      </Styled.RatingSection>

      <Styled.RatingSection>
        <InfoTitle
          dataTestId="review"
          title={getReviewTitle()}
          description={
            hasRating ? (
              plural('Review', 'Reviews', reviewCount)
            ) : (
              <>
                No reviews <Styled.ConditionalText>yet</Styled.ConditionalText>
              </>
            )
          }
          variant="SMALL"
        />
      </Styled.RatingSection>

      <Styled.RatingSection>
        <InfoTitle
          dataTestId="membership-duration"
          title={membershipDuration ?? 'New'}
          description="on DoneDeal"
          variant="SMALL"
        />
      </Styled.RatingSection>
    </Styled.PrivateSellerRatings>
  );
};

export { PrivateSellerRatings };
